import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { assert_false, assert_not_equal, assert_not_null, assert_true } from '@shared/asserts/utils_asserts';
import { ENUM__field_kind } from '../../_enums/ENUM__field_kind';
import { FN_FIELD__get_fields_primitive_map } from '../../_fields/_base/_methods/FN_FIELD__get_fields_primitive_map';
import { FIELD__base } from '../../_fields/_base/FIELD__base';
import { FIELD__list_base_generic } from '../../_fields/_primitives/list/FIELD__list_base_generic';
import { INVOCATION__base } from '../../_invocations/_base/INVOCATION__base';
import { DTO_GRAPH__edge } from '../DTO_GRAPH__edge';
import { DTO_GRAPH__instance } from '../DTO_GRAPH__instance';

type TYPE_OR_LIST<T extends FIELD__base> = T | FIELD__list_base_generic<T>;

declare module '../DTO_GRAPH__instance' {
  interface DTO_GRAPH__instance {
    /*
     * CREATE
     */
    edge__create<TYPE__field extends FIELD__base>(field_from: TYPE__field, field_to: TYPE__field): DTO_GRAPH__edge;
    /*
     * ADD
     */
    edge__try_add_for_fields<TYPE__field extends FIELD__base>(field_from: TYPE_OR_LIST<TYPE__field>, field_to: TYPE_OR_LIST<TYPE__field>): DTO_GRAPH__edge;

    edge__add_for_fields<TYPE__field extends FIELD__base>(field_from: TYPE_OR_LIST<TYPE__field>, field_to: TYPE_OR_LIST<TYPE__field>): DTO_GRAPH__edge;

    edge__add(edge: DTO_GRAPH__edge): DTO_GRAPH__edge;

    /*
     * EXISTS
     */

    edge__already_exists(edge: DTO_GRAPH__edge): boolean;

    edge__already_exists__for_fields<TYPE__field extends FIELD__base>(field_from: TYPE__field, field_to: TYPE__field): boolean;

    /*
     * GET
     */
    edge__get_for_fields<TYPE__field extends FIELD__base>(field_from: TYPE__field, field_to: TYPE__field): DTO_GRAPH__edge;

    edge__get<TYPE__field extends FIELD__base>(field_from: TYPE__field, field_to: TYPE__field): DTO_GRAPH__edge;

    edge__try_get_by_id(id: string): DTO_GRAPH__edge;

    edge__get_by_id(id: string): DTO_GRAPH__edge;

    edges__get_all(): DTO_GRAPH__edge[];

    edges__get_related_to_fields(fields: FIELD__base[]): DTO_GRAPH__edge[];

    edges__get_from(fields_from: FIELD__base[]): DTO_GRAPH__edge[];

    edges__get_to(fields_to: FIELD__base[]): DTO_GRAPH__edge[];

    edges__get_related_to_node(node: INVOCATION__base): DTO_GRAPH__edge[];

    /*
     * DELETE
     */
    edges__delete_field_related(fields_to: FIELD__base[]): void;

    edges__delete_to(fields_to: FIELD__base[]): void;

    edges__delete_from(fields_from: FIELD__base[]): void;

    edges__delete(edges_to_delete: DTO_GRAPH__edge[]): void;

    /*
     *
     */
    edge__replace(edge: DTO_GRAPH__edge): void;

    edges__ui_visible(): DTO_GRAPH__edge[];
  }
}

export function INIT_EXTENSION_DTO_GRAPH__instance_edges() {
  // do nothing
}

/*
 * CREATE
 */
DTO_GRAPH__instance.prototype.edge__create = function <TYPE__field extends FIELD__base>(field_from: TYPE__field, field_to: TYPE__field): DTO_GRAPH__edge {
  assert_true(field_from.is_field_is_compatible_to_sync_with(field_to));
  assert_true(field_to.is_field_is_compatible_to_sync_with(field_from));

  let out = new DTO_GRAPH__edge();

  [out.field_from__node_id, out.field_from__field_id] = [field_from.meta.node_id, field_from.meta.field_id];
  [out.field_to__node_id, out.field_to__field_id] = [field_to.meta.node_id, field_to.meta.field_id];

  [out.field_from__field_name, out.field_to__field_name] = [field_from.meta.field_name, field_to.meta.field_name];
  [out.field_from__node_name, out.field_to__node_name] = [field_from.meta.node_type, field_to.meta.node_type];

  [out.field_from__field_kind, out.field_to__field_kind] = [field_from.meta.field_kind, field_to.meta.field_kind];

  assert_not_equal(field_to.meta.field_kind, ENUM__field_kind.OUTPUT);

  out.assert_standard_fields();

  return out;
};

DTO_GRAPH__instance.prototype.edge__try_add_for_fields = function <TYPE__field extends FIELD__base>(
  field_from: TYPE__field,
  field_to: TYPE__field,
): DTO_GRAPH__edge {
  if (this.edge__already_exists__for_fields(field_from, field_to)) {
    let out = this.edge__get(field_from, field_to);
    return out;
  }

  this.edges__delete_to([field_to]);

  return this.edge__add_for_fields(field_from, field_to);
};

DTO_GRAPH__instance.prototype.edge__get_for_fields = function <TYPE__field extends FIELD__base>(
  field_from: TYPE__field,
  field_to: TYPE__field,
): DTO_GRAPH__edge {
  assert_true(this.edge__already_exists__for_fields(field_from, field_to));

  return this.edge__get(field_from, field_to);
};

/**
 * Add an edge to the graph. If an edge with the same source and destination already exists, an `AssertionError` is raised.
 * @param node_from The source node.
 * @param field_from The field of the source node.
 * @param node_to The destination node.
 * @param field_to The field of the destination node.
 * @returns The added edge.
 * @raises `AssertionError` if an edge with the same source and destination already exists.
 */
DTO_GRAPH__instance.prototype.edge__add_for_fields = function <TYPE__field extends FIELD__base>(
  field_from: TYPE_OR_LIST<TYPE__field>,
  field_to: TYPE_OR_LIST<TYPE__field>,
): DTO_GRAPH__edge {
  if (field_from instanceof FIELD__list_base_generic && field_to instanceof FIELD__list_base_generic) {
    let field_to__children = field_to.fields__get_map(true).values();
    this.edges__delete_field_related(field_to__children);

    field_to.items__remove_all(); //clear list

    this.field__replace(field_to);
  } else if (field_from instanceof FIELD__list_base_generic) {
    let item = field_from.create_item_new__default();
    field_from.items.push(item);
    this.field__replace(field_from);

    field_from = item; //use item as edge part
  } else if (field_to instanceof FIELD__list_base_generic) {
    let item = field_to.create_item_new__default();
    field_to.items.push(item);
    this.field__replace(field_to);

    field_to = item; //use item as edge part
  } else {
    let field_to__children = field_to.fields__get_map(true).values();
    this.edges__delete_field_related(field_to__children);
  }

  const edge = this.edge__create(field_from, field_to);

  return this.edge__add(edge);
};

function remove_edges_for_children() {}

/**
 * Add an edge to the graph. If an edge with the same source and destination already exists, an `AssertionError` is raised.
 * @param edge The edge to add.
 * @returns The added edge.
 */
DTO_GRAPH__instance.prototype.edge__add = function (edge: DTO_GRAPH__edge): DTO_GRAPH__edge {
  assert_false(this.edge__already_exists(edge), { assert_type: ENUM__error_type.GRAPH_EDGE__ALREADY_EXISTS, edge });
  edge.assert_standard_fields();
  assert_not_equal(edge.field_from__node_id, edge.field_to__node_id);

  let [node_from, node_to] = [this.node__get(edge.field_from__node_id), this.node__get(edge.field_to__node_id)];
  let [field_from, field_to] = [node_from.field__get(edge.field_from__field_id), node_to.field__get(edge.field_to__field_id)];

  assert_true(field_to.meta.data__synced_by_parent_id.is_empty_uuid()); //can't connect to field already synced by parent
  assert_false(field_to.meta.grid2d__is_dimension); //we cant connect edge to field dimension
  assert_not_equal(field_to.meta.field_kind, ENUM__field_kind.OUTPUT); //can't connect to output fields

  let field_to__children = FN_FIELD__get_fields_primitive_map(field_to, true); //or with dimension child
  field_to__children.delete(field_to.meta.field_id);

  for (let field_to__child of field_to__children.values()) assert_false(field_to__child.meta.grid2d__is_dimension);

  /*
   *
   */

  let edges_to = this.edges__get_to([field_to]);
  assert_true(edges_to.is_empty()); //can't connect to field already synced by parent

  let edges_to_children = this.edges__get_to(field_to__children.values());
  this.edges__delete(edges_to_children);

  /*
   *
   */

  this.edges.push(edge);

  this.field_source__link(field_from, field_to);

  /*
   *
   */

  return edge;
};

/*
 * EXISTS
 */

DTO_GRAPH__instance.prototype.edge__already_exists = function (edge: DTO_GRAPH__edge): boolean {
  let edge_reversed = edge.create_reversed();

  return this.edges.some(item => item.is_equals(edge) || item.is_equals(edge_reversed));
};

/**
 * Check if a graph has an edge.
 * @param node_from The source node.
 * @param field_from The field of the source node.
 * @param node_to The destination node.
 * @param field_to The field of the destination node.
 * @returns Whether the graph has the edge.
 */

DTO_GRAPH__instance.prototype.edge__already_exists__for_fields = function <TYPE__field extends FIELD__base>(
  field_from: TYPE__field,
  field_to: TYPE__field,
): boolean {
  if (field_to.meta.kind__is_output) [field_from, field_to] = [field_to, field_from];

  let is_field_from_list = field_from instanceof FIELD__list_base_generic;
  let is_field_to_list = field_to instanceof FIELD__list_base_generic;

  if (is_field_from_list && !is_field_to_list) return false;
  if (is_field_to_list && !is_field_from_list) return false;

  let edge = this.edge__create(field_from, field_to);

  return this.edge__already_exists(edge);
};

/**
 * Get an edge from the graph. If the edge does not exist, an `AssertionError` is raised.
 * @param node_from The source node.
 * @param field_from The field of the source node.
 * @param node_to The destination node.
 * @param field_to The field of the destination node.
 * @returns The edge.
 * @raises `AssertionError` if the edge does not exist.
 */
DTO_GRAPH__instance.prototype.edge__get = function <TYPE__field extends FIELD__base>(field_from: TYPE__field, field_to: TYPE__field): DTO_GRAPH__edge {
  let edge_to_find = this.edge__create(field_from, field_to);
  let edge_reversed = edge_to_find.create_reversed();

  const edge = this.edges.find_or_assert(e => e.is_equals(edge_to_find) || e.is_equals(edge_reversed));

  // `Edge ${GRAPH__edge.create(node_from.id.value, field_from, node_to.id.value, field_to)} not found`
  assert_not_null(edge);

  return edge;
};

DTO_GRAPH__instance.prototype.edge__try_get_by_id = function (id: string): DTO_GRAPH__edge {
  let out = this.edges.find(edge => edge.edge_id == id);

  if (!out) {
    out = DTO_GRAPH__edge.CREATE_STUB();
  }

  return out;
};

DTO_GRAPH__instance.prototype.edge__get_by_id = function (id: string): DTO_GRAPH__edge {
  return this.edges.find_or_assert(edge => edge.edge_id == id);
};

/**
 * Get all edges in the graph.
 * @returns The edges.
 */
DTO_GRAPH__instance.prototype.edges__get_all = function (): DTO_GRAPH__edge[] {
  return this.edges;
};

/*
 * EDGES GET
 */

DTO_GRAPH__instance.prototype.edges__get_related_to_fields = function (fields: FIELD__base[]): DTO_GRAPH__edge[] {
  let edges_from = this.edges__get_from(fields);
  let edges_to = this.edges__get_to(fields);

  let out = [...edges_from, ...edges_to];

  out = out.filter((edge, index) => out.findIndex(e => edge.is_equals(e)) === index); //get unique

  return out;
};

DTO_GRAPH__instance.prototype.edges__get_from = function (fields_from: FIELD__base[]): DTO_GRAPH__edge[] {
  let fields_ids_set = new Set(fields_from.map(field => field.meta.field_id));

  let out = this.edges.filter(edge => fields_ids_set.has(edge.field_from__field_id));

  return out;
};

DTO_GRAPH__instance.prototype.edges__get_to = function (fields_to: FIELD__base[]): DTO_GRAPH__edge[] {
  let fields_ids_set = new Set(fields_to.map(field => field.meta.field_id));

  let out = this.edges.filter(edge => fields_ids_set.has(edge.field_to__field_id));

  return out;
};

DTO_GRAPH__instance.prototype.edges__get_related_to_node = function (node: INVOCATION__base): DTO_GRAPH__edge[] {
  let out = this.edges.filter(edge => edge.field_from__node_id == node.meta.node_id || edge.field_to__node_id == node.meta.node_id);

  return out;
};

/*
 * EDGES DELETE
 */

DTO_GRAPH__instance.prototype.edges__delete_field_related = function (fields_to: FIELD__base[]): void {
  let edges_to = this.edges__get_to(fields_to);
  let edges_from = this.edges__get_from(fields_to);

  let edges__all = [...edges_to, ...edges_from];

  this.edges__delete(edges__all);
};

DTO_GRAPH__instance.prototype.edges__delete_to = function (fields_to: FIELD__base[]): void {
  let edges_to = this.edges__get_to(fields_to);
  this.edges__delete(edges_to);
};

DTO_GRAPH__instance.prototype.edges__delete_from = function (fields_from: FIELD__base[]): void {
  let edges_from = this.edges__get_from(fields_from);
  this.edges__delete(edges_from);
};

DTO_GRAPH__instance.prototype.edges__delete = function (edges_to_delete: DTO_GRAPH__edge[]): void {
  let fields_ids_to = [];

  for (let edge of edges_to_delete) {
    fields_ids_to.push(edge.field_to__field_id);

    let [field_from, field_to] = edge.get_fields(this);

    this.field_source__unlink(field_from, field_to);

    if (field_to.meta.data__populate_from_linked) {
      let field_from_source = field_from.find_source(this);
      if (field_from_source.meta.kind__is_input) field_to.populate_from(this, field_from);
    }

    this.edges = this.edges.filter(item => !item.is_equals(edge));
  }

  /*
   *
   */
  let nodes_ids = edges_to_delete.map(edge => edge.field_to__node_id).unique_values();

  for (let node_id of nodes_ids) {
    let node = this.node__get(node_id);

    node.fields_data__reset_if_not_linked(this);
  }
};

DTO_GRAPH__instance.prototype.edge__replace = function (edge: DTO_GRAPH__edge): void {
  const target_index = this.edges.findIndex(item => item.is_equals(edge));
  assert_not_equal(target_index, -1);

  this.edges[target_index] = edge;
};

DTO_GRAPH__instance.prototype.edges__ui_visible = function (): DTO_GRAPH__edge[] {
  let out = [...this.edges];

  out = out.filter(item => item.ui__is_visible);

  return out;
};
